import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Calendar, Clock, Newspaper } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/lib/seo";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import {
  CardGridSkeleton,
  EmptyState,
  QueryBoundary,
} from "@/components/states";
import { useBlogPosts } from "@/hooks/content";
import { IMAGES } from "@/data/site";

const ALL = "All";

const fmtDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export default function Blog() {
  const query = useBlogPosts();
  const [cat, setCat] = useState(ALL);

  // Depend on query.data directly: react-query keeps that reference stable,
  // whereas `query.data || []` would be a new array on every render and would
  // make the memo recompute each time.
  const categories = useMemo(() => {
    const seen = [];
    (query.data || []).forEach((p) => {
      if (p.category && !seen.includes(p.category)) seen.push(p.category);
    });
    return [ALL, ...seen];
  }, [query.data]);

  const allPosts = query.data || [];
  const posts = cat === ALL ? allPosts : allPosts.filter((p) => p.category === cat);

  return (
    <>
      <Seo
        title="Blog & Safety Insights"
        description="Practical articles and insights on workplace safety, fire safety, emergency preparedness, risk management and safety training from SKY Training & Consultancy."
        path="/blog"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }])}
      />
      <PageHero
        eyebrow="Insights"
        title="Blog & Safety Insights"
        description="Practical perspectives on workplace safety, fire safety, emergency preparedness and building a stronger safety culture."
        image={IMAGES.classroom}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }]}
      />

      <section className="section-pad">
        <div className="container-sky">
          <QueryBoundary
            query={query}
            skeleton={<CardGridSkeleton count={6} withImage className="grid gap-7 md:grid-cols-2 lg:grid-cols-3" />}
            empty={
              <EmptyState
                icon={Newspaper}
                title="No articles published yet"
                description="Safety insights and articles will appear here once they are published from the admin panel."
              />
            }
          >
            {() => (
              <>
                {categories.length > 2 && (
                  <div className="mb-10 flex flex-wrap gap-2.5">
                    {categories.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCat(c)}
                        aria-pressed={cat === c}
                        className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                          cat === c
                            ? "bg-brand-ink text-white shadow-md shadow-brand/25"
                            : "bg-white text-navy hover:bg-brand/10"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}

                <motion.div layout className="grid gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <AnimatePresence mode="popLayout">
                    {posts.map((post) => {
                      const published = fmtDate(post.publishedAt);
                      return (
                        <motion.article
                          key={post.id}
                          layout
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.96 }}
                          transition={{ duration: 0.4 }}
                          data-testid={`blog-card-${post.id}`}
                          className="group overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_4px_24px_rgba(3,56,106,0.05)] transition-all duration-500 hover:shadow-[0_18px_48px_rgba(10,126,201,0.14)]"
                        >
                          <Link to={`/blog/${post.slug}`} className="block">
                            {post.featuredImage && (
                              <div className="relative h-52 overflow-hidden">
                                <img
                                  src={post.featuredImage}
                                  alt={post.title}
                                  loading="lazy"
                                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                {post.category && (
                                  <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 font-mono-tech text-[10px] uppercase tracking-wider text-brand-ink backdrop-blur">
                                    {post.category}
                                  </span>
                                )}
                              </div>
                            )}
                            <div className="p-6">
                              <div className="flex items-center gap-4 text-xs text-slate-500">
                                {published && (
                                  <span className="flex items-center gap-1.5">
                                    <Calendar className="h-3.5 w-3.5" /> {published}
                                  </span>
                                )}
                                {post.readingTime && (
                                  <span className="flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5" /> {post.readingTime}
                                  </span>
                                )}
                              </div>
                              <h2 className="mt-3 font-heading text-lg font-bold leading-snug text-navy transition-colors group-hover:text-brand-ink">
                                {post.title}
                              </h2>
                              <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-slate-600">
                                {post.excerpt}
                              </p>
                              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-ink">
                                Read More
                                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                              </span>
                            </div>
                          </Link>
                        </motion.article>
                      );
                    })}
                  </AnimatePresence>
                </motion.div>

                {posts.length === 0 && (
                  <p className="text-sm text-slate-500">No articles in this category.</p>
                )}
              </>
            )}
          </QueryBoundary>
        </div>
      </section>

      <CTASection />
    </>
  );
}
